import { useEffect } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/TestSeriesSlice";
import { RootState } from "src/redux/store";
import { useDeleteTestSeriesMutation, useGetAllTestSeriesWithPaginationQuery } from "src/services/TestSeriesServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import TestSeriesListing from "./TestSeriesListing";
import { Chip, Tooltip } from "@mui/material";

const TestSeriesListingWrapper = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate()

  const [deleteTestSeries] = useDeleteTestSeriesMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.testSeries);

  const { data, isLoading, isFetching } = useGetAllTestSeriesWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "testTitle"
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (jobId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          navigate(`edit/${jobId}`);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.TEST_SERIES,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Test Series ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteTestSeries({ id: jobId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.TEST_SERIES,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "testTitle",
      headerName: "Test Series Title",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.testTitle} </div>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.price ? row?.price : "Free"} </div>
      ),
    },
    {
      field: "mrp",
      headerName: "Offer Price",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.mrp ? row?.mrp : 0} </div>
      ),
    },
    {
      field: "testDescription",
      headerName: "Description",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120px]",
      renderCell: (row: any) => (
        <Tooltip title={row?.testDescription}>
          <div className="capitalize min-w-[120px] line-clamp-2"> {row?.testDescription} </div>
        </Tooltip>
      ),
    },

    {
      field: "seriesTitle",
      headerName: "Technology",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize flex"> {row?.testTechnologyDetails?.map((technology: any) => {
          return (
            <div className="flex gap-1">
              <Chip
                key={technology?._id}
                label={technology?.questionTechnology}
                className="me-2 my-1"
              />
            </div>
          )
        })} </div>
      ),
    },
    {
      field: "testTag",
      headerName: "Tag",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize flex"> {row?.testTag?.map((el: any) => {
          return (
            <div className=" gap-1">
              <Chip
                key={el?._id}
                label={el}
                className="me-2 my-1"
              />
            </div>
          )
        })} </div>
      ),
    },
    {
      field: "testTag",
      headerName: "Questions",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize">{row?.questionId?.length} </div>
      ),
    },
    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <TestSeriesListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
    </div>
  );
};

export default TestSeriesListingWrapper;
